import { Divider, Section, Stack } from '@cmg/design-system';
import { FormattedDate, FormattedNumber, useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';

import { BooleanIcon } from '../../../../design-system/components/data-display/boolean-icon/BooleanIcon';
import { FlagWithNote } from '../../../../design-system/components/data-display/flag-with-note/FlagWithNote';
import { FilingType } from '../../../../graphql';
import { useOfProceedsLabels } from '../../../../types/domain/filing/constants';
import { RANK_LABELS } from '../../../offering-setup-next/pricing/forms/DeliveryInstrumentForm';
import {
  OfferingDetails_PublishedOffering_InstrumentPartsFragment,
  OfferingDetails_PublishedOffering_TermsFragment,
  OfferingDetails_PublishedOfferingFragment,
} from '../../../offering-side-panel/offering-details/graphql/__generated__';
import { KeyValuePair } from '../KeyValuePair';
import { TermsDropdownButton } from './components/TermsDropdownButton';

type ConvertibleTermsSectionProps = {
  terms: OfferingDetails_PublishedOffering_TermsFragment;
  pricingInstrument: OfferingDetails_PublishedOffering_InstrumentPartsFragment | null;
  pricingCurrencyCode: OfferingDetails_PublishedOfferingFragment['pricingCurrencyCode'];
  offeringStatus?: OfferingDetails_PublishedOfferingFragment['status'];
};

export function ConvertibleTermsSection({
  terms,
  pricingInstrument,
  pricingCurrencyCode,
}: Readonly<ConvertibleTermsSectionProps>) {
  const intl = useIntl();
  const [latestFilling, setLatestFiling] = useState(
    terms.filings.length ? terms.filings[terms.filings.length - 1] : undefined
  );

  const titleItems = latestFilling
    ? [
        <TermsDropdownButton
          key="terms-dropdown-button"
          terms={terms}
          latestFilling={latestFilling}
          setLatestFiling={setLatestFiling}
        />,
      ]
    : [];

  return (
    <Section title="Terms" titleItems={titleItems} data-testid="convertible-terms-section">
      <KeyValuePair label="Offering Currency" value={pricingCurrencyCode} />
      <KeyValuePair
        label="Rank"
        value={defaultTo(RANK_LABELS[pricingInstrument?.rank ?? ''], '-')}
      />

      <Stack py={1}>
        <Divider />
      </Stack>

      <KeyValuePair
        label="Principal Amount"
        value={
          <FormattedNumber
            value={latestFilling?.sizeInCurrency}
            format="currencyCompact"
            currency={pricingCurrencyCode}
          />
        }
      />
      <KeyValuePair
        label="Overallotment Authorized"
        value={
          <FormattedNumber
            value={latestFilling?.notionalOverAllotmentAuthorized}
            format="currencyCompact"
            currency={pricingCurrencyCode}
          />
        }
      />
      <KeyValuePair
        label="Par Value"
        value={
          <FormattedNumber
            value={latestFilling?.parValue}
            format="currency"
            currency={pricingCurrencyCode}
          />
        }
      />
      {latestFilling?.filingType === FilingType.Final ? (
        <React.Fragment>
          <KeyValuePair
            label="Offer Price"
            value={
              <FormattedNumber
                value={latestFilling?.offerPrice}
                format="currency"
                currency={pricingCurrencyCode}
              />
            }
          />
          <KeyValuePair
            label="Coupon"
            value={
              <FormattedNumber
                value={latestFilling?.coupon}
                format="percent"
                maximumFractionDigits={4}
                minimumFractionDigits={4}
              />
            }
          />
          <KeyValuePair
            label="Premium"
            value={<FormattedNumber value={latestFilling?.premium} format="percent" />}
          />
          <KeyValuePair
            label="Conversion Price"
            value={
              <FormattedNumber
                value={latestFilling?.conversionPrice}
                format="currency"
                currency={pricingCurrencyCode}
              />
            }
          />
          <KeyValuePair
            label="Conversion Ratio"
            value={<FormattedNumber value={latestFilling?.initialConversionRatio} format="float" />}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <KeyValuePair
            label="Coupon Talk"
            value={
              <React.Fragment>
                <FormattedNumber
                  value={latestFilling?.couponTalkLow}
                  format="percent"
                  emptyValueLabel=""
                  maximumFractionDigits={4}
                  minimumFractionDigits={4}
                />
                –
                <FormattedNumber
                  value={latestFilling?.couponTalkHigh}
                  format="percent"
                  emptyValueLabel=""
                  maximumFractionDigits={4}
                  minimumFractionDigits={4}
                />
              </React.Fragment>
            }
          />
          <KeyValuePair
            label="Premium Talk"
            value={
              <React.Fragment>
                <FormattedNumber
                  value={latestFilling?.premiumTalkLow}
                  format="percent"
                  emptyValueLabel=""
                />
                –
                <FormattedNumber
                  value={latestFilling?.premiumTalkHigh}
                  format="percent"
                  emptyValueLabel=""
                />
              </React.Fragment>
            }
          />
        </React.Fragment>
      )}

      <Stack py={1}>
        <Divider />
      </Stack>

      <KeyValuePair
        label="Maturity Date"
        value={<FormattedDate value={terms.maturityDate} format="literal" timeZone="UTC" />}
      />
      <KeyValuePair
        label="Callable"
        value={<BooleanIcon value={defaultTo(terms?.callable, false)} />}
      />
      <KeyValuePair
        label="Call Date(s)"
        value={
          isEmpty(terms.callDates ?? [])
            ? '-'
            : terms.callDates
                ?.map(date => intl.formatDate(date, { format: 'literal', timeZone: 'UTC' }))
                .join(', ')
        }
      />
      <KeyValuePair
        label="Provisional Call"
        value={
          <FlagWithNote
            flag={defaultTo(terms?.provisionCall?.enabled, false)}
            note={defaultTo(terms?.provisionCall?.notes, null)}
            notesOnSameLine={true}
          />
        }
      />
      <KeyValuePair
        label="Puttable"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.puttable?.enabled, false)}
            note={defaultTo(terms?.puttable?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Perpetual"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.perpetual?.enabled, false)}
            note={defaultTo(terms?.perpetual?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Contingent Payment"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.contingentPayment?.enabled, false)}
            note={defaultTo(terms?.contingentPayment?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Contingent Conversion"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            // slotProps={flagWithNoteSlotProps}
            flag={defaultTo(terms?.contingentConversion?.enabled, false)}
            note={defaultTo(terms?.contingentConversion?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Dividend Protection"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.dividendProtection?.enabled, false)}
            note={defaultTo(terms?.dividendProtection?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Hedging"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.hedging?.enabled, false)}
            note={defaultTo(terms?.hedging?.notes, null)}
          />
        }
      />
      <KeyValuePair
        label="Change of Control"
        value={
          <FlagWithNote
            notesOnSameLine={true}
            flag={defaultTo(terms?.changeOfControl?.enabled, false)}
            note={defaultTo(terms?.changeOfControl?.notes, null)}
          />
        }
      />

      <Stack py={1}>
        <Divider />
      </Stack>

      <KeyValuePair
        label="Use of Proceeds"
        value={
          latestFilling?.useOfProceeds?.length
            ? latestFilling.useOfProceeds
                .map(useOfProceed => useOfProceedsLabels[useOfProceed])
                .join(', ')
            : '-'
        }
      />
    </Section>
  );
}
